import { useCallback, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { queueScrollToFirstFormFieldError, useApiCall } from "@porchpass/ui";
import { ApiError } from "@porchpass/api-client";
import { useUser } from "src/context";
import { captureApplicationIds } from "src/api";
import { type FormErrorShape } from "src/utils";
import { isDuplicateCoborrowerConfirmationError } from "src/utils/duplicateCoborrower";
import { useApiErrorHandler } from "./useApiErrorHandler";

type DuplicateCoborrowerPromptState = {
  isOpen: boolean;
  borrowerName: string;
  confirm: () => void;
  cancel: () => void;
};

type UseDlaFormSubmitOptions<TVariables> = {
  duplicateCoborrowerPrompt?: boolean | ((data: TVariables) => string);
  toast?: {
    success?: string | false;
    error?: string | false;
    useServerErrorMessage?: boolean;
  };
  onSuccess?: (data: unknown) => void;
};

const getDuplicateBorrowerName = <T extends Record<string, unknown>>(
  data: T,
  getBorrowerDisplayName?: boolean | ((data: T) => string),
) => {
  if (typeof getBorrowerDisplayName === "function") {
    return getBorrowerDisplayName(data);
  }

  const firstName = typeof data.first_name === "string" ? data.first_name : "";
  const lastName = typeof data.last_name === "string" ? data.last_name : "";
  const fullName = `${firstName} ${lastName}`.trim();
  if (fullName) return fullName;

  if (typeof data.email === "string" && data.email) return data.email;
  if (typeof data.phone_number === "string" && data.phone_number) {
    return data.phone_number;
  }

  return "This person";
};

export function useDlaFormSubmit<TVariables extends Record<string, unknown>>(
  apiFn: (variables: TVariables) => Promise<unknown>,
  options: UseDlaFormSubmitOptions<TVariables> = {},
) {
  const { setUserData } = useUser();
  const { handleApiError } = useApiErrorHandler();
  const [hasAttemptedFormSubmission, setHasAttemptedFormSubmission] =
    useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [response, setResponse] = useState<Record<string, unknown> | null>(
    null,
  );
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [duplicateBorrowerName, setDuplicateBorrowerName] = useState("");
  const pendingSubmitRef = useRef<{
    data: TVariables;
    formErrors?: FormErrorShape<TVariables>;
  } | null>(null);

  const submitApi = useApiCall(apiFn, {
    toast: options.toast ?? { success: false, error: false },
    onSuccess: (data) => {
      if (data && typeof data === "object") {
        if (setUserData) {
          captureApplicationIds(data, setUserData);
        }
        setResponse({
          ...(data as Record<string, unknown>),
          _submittedAt: Date.now(),
        });
      } else {
        setResponse({ _submittedAt: Date.now() });
      }
      setApiError(null);
      setIsDuplicateModalOpen(false);
      pendingSubmitRef.current = null;
      options.onSuccess?.(data);
    },
    onError: (error) => {
      handleApiError(error);
      setApiError(error.message);
    },
  });

  const submitHandler = useCallback(
    async (
      data: TVariables,
      formErrors?: FormErrorShape<TVariables>,
      ignoreDuplicate = false,
    ) => {
      flushSync(() => {
        setHasAttemptedFormSubmission(true);
      });

      if (formErrors && Object.keys(formErrors).length > 0) {
        const hasBlockingErrors = Object.values(formErrors).some(
          (error) => error?.blockSubmit,
        );
        if (hasBlockingErrors) {
          queueScrollToFirstFormFieldError();
          return;
        }
      }

      const submitData = ignoreDuplicate
        ? { ...data, ignore_duplicate: true }
        : data;

      try {
        const result = await submitApi.execute(submitData);
        if (result !== undefined) {
          return;
        }
      } catch (error) {
        const message =
          error instanceof ApiError
            ? error.message
            : error instanceof Error
              ? error.message
              : "";

        if (
          options.duplicateCoborrowerPrompt &&
          !ignoreDuplicate &&
          isDuplicateCoborrowerConfirmationError(message)
        ) {
          pendingSubmitRef.current = { data, formErrors };
          setDuplicateBorrowerName(
            getDuplicateBorrowerName(data, options.duplicateCoborrowerPrompt),
          );
          setIsDuplicateModalOpen(true);
          setApiError(null);
          return;
        }
      }
    },
    [options.duplicateCoborrowerPrompt, submitApi],
  );

  const duplicateCoborrowerPromptState: DuplicateCoborrowerPromptState | null =
    options.duplicateCoborrowerPrompt
      ? {
          isOpen: isDuplicateModalOpen,
          borrowerName: duplicateBorrowerName,
          confirm: () => {
            const pendingSubmit = pendingSubmitRef.current;
            if (!pendingSubmit) return;
            void submitHandler(
              pendingSubmit.data,
              pendingSubmit.formErrors,
              true,
            );
          },
          cancel: () => {
            setIsDuplicateModalOpen(false);
            pendingSubmitRef.current = null;
          },
        }
      : null;

  return [
    submitHandler,
    submitApi.isLoading,
    response,
    hasAttemptedFormSubmission,
    apiError,
    duplicateCoborrowerPromptState,
  ] as const;
}
